import React from 'react'
import { Box, Skeleton, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { C, RADIUS, SX, TYPE } from '../../merchants/shared/tokens'

/** Value colour per tone; the label and sub text always say the state too, so colour is never the only signal. */
const TONE_FG = { ok: C.ok, warn: C.warn, danger: C.danger }

/**
 * The KPI cards every PTL Merchant Setup screen opens with. A card with `onClick` is also a filter for the content
 * below it (aria-pressed shows which one is on).
 *
 * @param items    [{ key, label, value, sub, tone: ok | warn | danger, onClick, active, hint, to }]; `to` makes the card a link to another screen
 * @param loading  shows placeholders of the same size, so nothing jumps when the numbers arrive
 * @param minWidth narrowest card in px before the row wraps (default 160)
 */
export default function KpiStrip({ items, loading, ariaLabel = 'Key figures', minWidth = 160 }) {
  return (
    <Box role='group' aria-label={ariaLabel} sx={{
      // Cards never get narrower than their label: they wrap to fewer columns instead of clipping.
      display: 'grid', gap: 1.25, mb: 2.5, gridTemplateColumns: `repeat(auto-fit, minmax(min(${minWidth}px, 100%), 1fr))`,
    }}>
      {items.map((k) => <KpiCard key={k.key || k.label} {...k} loading={loading} />)}
    </Box>
  )
}

function KpiCard({ label, value, sub, tone, onClick, active, hint, to, loading }) {
  const clickable = (!!onClick || !!to) && !loading
  const body = (
    <>
      <Typography component='span' sx={{ ...TYPE.eyebrow, display: 'block', fontSize: 10, color: active ? C.accent : C.inkFaint }}>{label}</Typography>
      {loading ? (
        <Skeleton variant='text' width='55%' sx={{ fontSize: 24, mt: 0.25 }} />
      ) : (
        <Typography component='span' sx={{
          display: 'block', fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', mt: 0.25, lineHeight: 1.25,
          fontVariantNumeric: 'tabular-nums', color: TONE_FG[tone] || C.ink, overflowWrap: 'anywhere',
        }}>
          {value}
        </Typography>
      )}
      {loading ? (
        <Skeleton variant='text' width='80%' sx={{ fontSize: 12 }} />
      ) : (
        sub && <Box component='span' sx={{ display: 'block', fontSize: 11.5, color: C.inkMuted, mt: 0.25, lineHeight: 1.4 }}>{sub}</Box>
      )}
    </>
  )
  const frame = {
    ...SX.card, minWidth: 0, minHeight: 88, px: 1.75, py: 1.5, boxSizing: 'border-box', textAlign: 'left',
    borderColor: active ? C.accent : C.line, bgcolor: active ? C.accentSoft : C.surface,
  }
  if (!clickable) return <Box sx={frame}>{body}</Box>
  const as = to ? { component: RouterLink, to } : { component: 'button', type: 'button', onClick, 'aria-pressed': !!active }
  return (
    <Box {...as} title={hint}
      sx={{
        ...frame, font: 'inherit', cursor: 'pointer', display: 'block', width: '100%', textDecoration: 'none', color: 'inherit',
        transition: 'border-color 150ms, background-color 150ms, box-shadow 150ms',
        '&:hover': { borderColor: active ? C.accent : C.accentBorder, boxShadow: 'none' },
        '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 },
        borderRadius: RADIUS.md,
      }}>
      {body}
    </Box>
  )
}
